// src/pages/Administration/Settings/NotificationChannelsTab.tsx
/**
 * Which notifications a module sends, and down which channels.
 *
 * One card per notification type: a headline switch that turns the whole topic on or off, and four
 * channel switches under it. Built from `@/components/ui`, like the rest of this page.
 *
 * ⚠️ **Saves on every toggle, not on the page's Save button.** Each row is its own record with its own
 * PUT, so batching them behind one button would mean a half-applied save leaves the screen disagreeing
 * with the server about which topics are live. A toggle is applied optimistically and **reverted** if
 * the write is refused, so the switch never shows a state the server did not accept.
 *
 * ⚠️ **`isActive` here is a kill switch, not a soft-delete.** On the flat settings side `isActive`
 * means "this row is not in use"; on a channel row it means "this topic is live". Same field name,
 * opposite kind of thing, so it is labelled as the topic itself rather than as "in use".
 *
 * ⚠️ **Topic switches stay editable while the module master is off.** An office configures the topics
 * it wants before turning notifications back on, and disabling them here would make that impossible.
 * The backend short-circuits on the master regardless, so nothing sends in the meantime.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IconAlertTriangle, IconBell, IconBrandWhatsapp, IconDeviceMobileMessage, IconMail, IconRefresh,
} from '@tabler/icons-react';
import {
  Alert, Badge, Box, Button, Divider, Group, Paper, Skeleton, Stack, Switch, Text, ThemeIcon,
} from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';

/**
 * The row shape, structural so one component serves both modules.
 *
 * `ACCareNotificationChannelSetting` and `ACCourseNotificationChannelSetting` are field-for-field
 * identical, the same way the flat `SettingService` type covers both settings services.
 */
export interface ChannelRow {
  settingID: number;
  notificationTypeID: number;
  notificationTypeName: string;
  displayName: string;
  description?: string | null;
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  whatsAppEnabled: boolean;
  isCritical: boolean;
  priority: number;
  isActive: boolean;
}

/** The four delivery channels, in the order they are offered. */
const CHANNELS = [
  { key: 'emailEnabled', label: 'Email', icon: IconMail },
  { key: 'pushEnabled', label: 'Push', icon: IconBell },
  { key: 'smsEnabled', label: 'SMS', icon: IconDeviceMobileMessage },
  { key: 'whatsAppEnabled', label: 'WhatsApp', icon: IconBrandWhatsapp },
] as const;

type ChannelKey = typeof CHANNELS[number]['key'];
/** Everything this tab may write. `isActive` is the topic switch; the rest are the channels. */
type WritableKey = ChannelKey | 'isActive';

/** What both channel services provide. Only the two methods this tab uses. */
export interface ChannelService {
  getAll: () => Promise<unknown>;
  update: (id: number, patch: Record<string, boolean>) => Promise<unknown>;
}

export interface NotificationChannelsTabProps {
  service: ChannelService;
  /** "Counselling" / "Grow" — wording only. */
  moduleLabel: string;
  /** The page-wide search term, so a search does not silently exclude this tab. */
  search?: string;
  /** True when the module master switch is off, so the tab can say nothing is sending. */
  notificationsPaused?: boolean;
}

/** Reads through the `ResponseData` envelope, as elsewhere in this app. */
function unwrapRows(res: unknown): ChannelRow[] {
  const raw = (res as { data?: unknown })?.data ?? res;
  return Array.isArray(raw) ? (raw as ChannelRow[]) : [];
}

export default function NotificationChannelsTab({
  service, moduleLabel, search = '', notificationsPaused = false,
}: NotificationChannelsTabProps) {
  const [rows, setRows] = useState<ChannelRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Rows with a write in flight, so a switch cannot be double-toggled mid-request. */
  const [busy, setBusy] = useState<Set<number>>(new Set());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = unwrapRows(await service.getAll());
      // Stable order: the backend's own priority first, then alphabetical so equal priorities do not
      // shuffle between loads.
      setRows([...list].sort(
        (a, b) => (a.priority - b.priority) || a.displayName.localeCompare(b.displayName),
      ));
    } catch (e) {
      console.error(`[Settings] failed to load ${moduleLabel} notification channels:`, e);
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [service, moduleLabel]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /**
   * Applies one toggle.
   *
   * ⚠️ Optimistic, then reverted on failure. The alternative — waiting for the round trip — makes a
   * switch feel broken on a slow connection, and the alternative to reverting is a switch that shows a
   * setting the server never accepted, which is worse than either.
   */
  const toggle = async (row: ChannelRow, field: WritableKey, next: boolean) => {
    if (busy.has(row.settingID)) return;

    setBusy((prev) => new Set(prev).add(row.settingID));
    setRows((prev) => prev.map(
      (r) => (r.settingID === row.settingID ? { ...r, [field]: next } : r),
    ));

    try {
      // Only the changed field goes out — the update request is defined as a partial.
      await service.update(row.settingID, { [field]: next });
    } catch (e) {
      console.error(`[Settings] could not update ${row.notificationTypeName}.${field}:`, e);
      setRows((prev) => prev.map(
        (r) => (r.settingID === row.settingID ? { ...r, [field]: !next } : r),
      ));
      toast.error(
        'Could not update',
        `${row.displayName} was left as it was. ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setBusy((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(row.settingID);
        return nextSet;
      });
    }
  };

  const term = search.trim().toLowerCase();
  const visible = useMemo(() => (term
    ? rows.filter((r) => r.displayName.toLowerCase().includes(term)
      || (r.description ?? '').toLowerCase().includes(term)
      || r.notificationTypeName.toLowerCase().includes(term))
    : rows), [rows, term]);

  const liveCount = rows.filter((r) => r.isActive).length;

  if (loadError) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        type="error"
        title={`Could not load the ${moduleLabel} notification channels`}
      >
        <Stack gap="xs" align="flex-start">
          <Text size="sm">{loadError}</Text>
          <Button size="xs" variant="light" onClick={fetchAll}>Try again</Button>
        </Stack>
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* This tab keeps its own bar: its rows save as they are switched, so the page's Save button
          does not apply to them and pairing them under it would misrepresent both. */}
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="xs" align="center">
          <Text size="sm" c="dimmed">
            {rows.length} notification type{rows.length === 1 ? '' : 's'}
          </Text>
          {rows.length > 0 && (
            <Badge size="sm" variant="light" color={liveCount > 0 ? 'teal' : 'gray'}>
              {liveCount} live
            </Badge>
          )}
        </Group>
        <Group gap="sm">
          <Text size="xs" c="dimmed">Changes here save as you switch them.</Text>
          <Button
            variant="default"
            size="sm"
            leftSection={<IconRefresh size={16} />}
            onClick={fetchAll}
            loading={loading}
          >
            Reload
          </Button>
        </Group>
      </Group>

      {notificationsPaused && (
        <Alert type="warning" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            Notifications are switched off for {moduleLabel}, so nothing below is sending. The topics
            stay editable, so you can set them up before turning notifications back on under General.
          </Text>
        </Alert>
      )}

      {loading && rows.length === 0 ? (
        <Stack gap="md">
          {Array.from({ length: 4 }).map((_, i) => (
            <Paper key={i} p="lg" radius="md" withBorder>
              <Skeleton height={16} width="34%" mb="sm" radius="sm" />
              <Skeleton height={12} width="60%" mb="lg" radius="sm" />
              <Skeleton height={28} radius="sm" />
            </Paper>
          ))}
        </Stack>
      ) : visible.length === 0 ? (
        <Paper p="xl" radius="md" withBorder>
          <Text size="sm" c="dimmed" ta="center">
            {rows.length === 0
              ? 'No notification types are configured for this module.'
              : 'No notification types match that search. Clear the search box to see them all.'}
          </Text>
        </Paper>
      ) : (
        visible.map((row) => {
          const rowBusy = busy.has(row.settingID);
          return (
            <Paper key={row.settingID} p="lg" radius="md" withBorder>
              <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                <Switch
                  style={{ flex: 1 }}
                  label={row.displayName || row.notificationTypeName}
                  description={row.description ?? undefined}
                  checked={row.isActive}
                  disabled={rowBusy}
                  onChange={(e) => toggle(row, 'isActive', e.currentTarget.checked)}
                />
                {row.isCritical && (
                  <Badge size="sm" variant="light" color="orange">Critical</Badge>
                )}
              </Group>

              <Divider my="md" />

              {/* Dimmed and disabled when the topic is off: the channels are moot, and leaving them
                  live would suggest a message could still go out down one of them. */}
              <Box style={{ opacity: row.isActive ? 1 : 0.5 }}>
                <Group gap="lg" wrap="wrap">
                  {CHANNELS.map(({ key, label, icon: Icon }) => (
                    <Group key={key} gap={6} wrap="nowrap">
                      <ThemeIcon size="sm" radius="sm" variant="light" color="gray">
                        <Icon size={14} />
                      </ThemeIcon>
                      <Switch
                        label={label}
                        checked={row[key]}
                        disabled={!row.isActive || rowBusy}
                        onChange={(e) => toggle(row, key, e.currentTarget.checked)}
                      />
                    </Group>
                  ))}
                </Group>

                {row.isActive
                  && !CHANNELS.some(({ key }) => row[key]) && (
                  <Text size="xs" c="dimmed" mt="sm">
                    This topic is on but has no channel selected, so nothing will reach anybody.
                  </Text>
                )}
              </Box>
            </Paper>
          );
        })
      )}
    </Stack>
  );
}
